import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { indianCities, vehicleTypes } from "./listingConstants";

/**
 * Cargo Listing Form Component
 * 
 * Form for creating cargo space listings with transport mode, route,
 * capacity, pricing, and optional dimensions.
 */
export function AddCargoForm({ form, setForm, onSubmit, loading }) {
  return (
    <form onSubmit={onSubmit} className="space-y-6 max-w-2xl">
      {/* Transport Mode & Vehicle Type */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Transport Mode</Label>
          <Select
            value={form.transport_mode}
            onValueChange={(value) => setForm({ ...form, transport_mode: value, vehicle_type: "" })}
          >
            <SelectTrigger className="rounded-none" data-testid="cargo-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="road">Road</SelectItem>
              <SelectItem value="rail">Rail</SelectItem>
              <SelectItem value="air">Air</SelectItem>
              <SelectItem value="water">Water</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Vehicle Type</Label>
          <Select
            value={form.vehicle_type}
            onValueChange={(value) => setForm({ ...form, vehicle_type: value })}
          >
            <SelectTrigger className="rounded-none" data-testid="cargo-vehicle-select">
              <SelectValue placeholder="Select vehicle" />
            </SelectTrigger>
            <SelectContent>
              {vehicleTypes[form.transport_mode]?.map((type) => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Origin & Destination */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Origin City</Label>
          <Select
            value={form.origin_city}
            onValueChange={(value) => setForm({ ...form, origin_city: value })}
          >
            <SelectTrigger className="rounded-none" data-testid="cargo-origin-select">
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent>
              {indianCities.map((city) => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Destination City</Label>
          <Select
            value={form.destination_city}
            onValueChange={(value) => setForm({ ...form, destination_city: value })}
          >
            <SelectTrigger className="rounded-none" data-testid="cargo-destination-select">
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent>
              {indianCities.map((city) => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Date & Time */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Departure Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start rounded-none" data-testid="cargo-date-picker">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {form.departure_date ? format(form.departure_date, "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={form.departure_date}
                onSelect={(date) => setForm({ ...form, departure_date: date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="space-y-2">
          <Label>Departure Time</Label>
          <Input
            type="time"
            value={form.departure_time}
            onChange={(e) => setForm({ ...form, departure_time: e.target.value })}
            required
            className="rounded-none"
            data-testid="cargo-time-input"
          />
        </div>
      </div>

      {/* Capacity & Pricing */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Available Capacity (kg)</Label>
          <Input
            type="number"
            placeholder="e.g. 5000"
            value={form.available_capacity}
            onChange={(e) => setForm({ ...form, available_capacity: e.target.value })}
            required
            className="rounded-none"
            data-testid="cargo-capacity-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Price per kg (INR)</Label>
          <Input
            type="number"
            placeholder="e.g. 50"
            value={form.price_per_kg}
            onChange={(e) => setForm({ ...form, price_per_kg: e.target.value })}
            required
            className="rounded-none"
            data-testid="cargo-price-input"
          />
        </div>
      </div>

      {/* Space Dimensions (Optional) */}
      <div className="border border-border p-4 space-y-4">
        <h4 className="font-semibold text-sm uppercase tracking-wide">Space Dimensions (Optional)</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Length (cm)</Label>
            <Input
              type="number"
              placeholder="e.g. 400"
              value={form.space_length}
              onChange={(e) => setForm({ ...form, space_length: e.target.value })}
              className="rounded-none"
              data-testid="cargo-length-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Width (cm)</Label>
            <Input
              type="number"
              placeholder="e.g. 200"
              value={form.space_width}
              onChange={(e) => setForm({ ...form, space_width: e.target.value })}
              className="rounded-none"
              data-testid="cargo-width-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Height (cm)</Label>
            <Input
              type="number"
              placeholder="e.g. 200"
              value={form.space_height}
              onChange={(e) => setForm({ ...form, space_height: e.target.value })}
              className="rounded-none"
              data-testid="cargo-height-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Max Weight Capacity (kg)</Label>
            <Input
              type="number"
              placeholder="e.g. 10000"
              value={form.max_weight}
              onChange={(e) => setForm({ ...form, max_weight: e.target.value })}
              className="rounded-none"
              data-testid="cargo-max-weight-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Estimated ETA (hours)</Label>
            <Input
              type="number"
              placeholder="e.g. 24"
              value={form.eta_hours}
              onChange={(e) => setForm({ ...form, eta_hours: e.target.value })}
              className="rounded-none"
              data-testid="cargo-eta-input"
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label>Description (Optional)</Label>
        <Textarea
          placeholder="Add any additional details about your cargo space..."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="rounded-none"
          data-testid="cargo-description-input"
        />
      </div>

      {/* Contact Details */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Contact Phone</Label>
          <Input
            type="tel"
            value={form.contact_phone}
            onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            required
            className="rounded-none"
            data-testid="cargo-phone-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Contact Email</Label>
          <Input
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            required
            className="rounded-none"
            data-testid="cargo-email-input"
          />
        </div>
      </div>

      <Button 
        type="submit" 
        className="rounded-none btn-hover bg-accent text-accent-foreground hover:bg-accent/90"
        disabled={loading || !form.departure_date}
        data-testid="submit-cargo-listing"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Listing"}
      </Button>
    </form>
  );
}
